/**
 * Dial Connect section -- Google Calendar, Google Contacts, Apple Contacts,
 * WhatsApp chat export, LinkedIn takeout, Uber+Ola, MMT bookings.
 *
 * Demo mode imports realistic mock data immediately. File-drop inputs accept
 * WhatsApp chat ZIPs and LinkedIn export ZIPs. vCard upload covers Apple
 * Contacts on the web build.
 *
 * Each successful import dispatches `dial:connector_imported` -- the
 * relationship-graph and rank-by-kind surfaces listen and refresh.
 */
import {
  DIAL_CONNECTORS,
  fetchCalendarEvents,
  fetchGContacts,
  parseVcard,
  parseWaChat,
  parseLinkedInZip,
  fetchTrips,
  fetchTravel,
} from '../connectors/lifestyle/index.ts';
import type {
  ConnectMeta,
  ConnectorId,
  ContactSeed,
  CalendarEvent,
  InteractionStat,
  TripRecord,
  TravelBooking,
  LinkedInImport,
} from '../connectors/lifestyle/index.ts';

interface ConnectorBadge {
  status: 'idle' | 'connected';
  last_sync_ts?: number;
  count?: number;
}

const STORAGE_KEY = 'dial:connectors:v0';

type PreviewKind =
  | { kind: 'contacts'; rows: readonly ContactSeed[] }
  | { kind: 'events'; rows: readonly CalendarEvent[] }
  | { kind: 'interactions'; rows: readonly InteractionStat[] }
  | { kind: 'linkedin'; rows: LinkedInImport }
  | { kind: 'trips'; rows: readonly TripRecord[] }
  | { kind: 'travel'; rows: readonly TravelBooking[] };

const FILE_DROP_IDS: ReadonlySet<ConnectorId> = new Set(['wa-chat-export', 'linkedin', 'apple-contacts']);

export class DialConnectSection extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #state: Record<ConnectorId, ConnectorBadge> = loadState();
  #preview: { id: ConnectorId; data: PreviewKind } | null = null;
  #busy: ConnectorId | null = null;

  connectedCallback(): void {
    this.#render();
  }

  async #connect(id: ConnectorId, file?: File): Promise<void> {
    if (this.#busy) return;
    this.#busy = id;
    this.#render();
    try {
      const data = await runConnector(id, file);
      this.#preview = { id, data };
      const count = data.kind === 'linkedin'
        ? data.rows.contacts.length + data.rows.work.length
        : data.rows.length;
      this.#state = {
        ...this.#state,
        [id]: { status: 'connected', last_sync_ts: Date.now(), count },
      };
      saveState(this.#state);
      this.dispatchEvent(new CustomEvent('dial:connector_imported', { detail: { id, data }, bubbles: true, composed: true }));
      window.dispatchEvent(new CustomEvent('dial:connector_imported', { detail: { id, data } }));
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.warn(`[dial] connector ${id} failed:`, msg);
    } finally {
      this.#busy = null;
      this.#render();
    }
  }

  #disconnect(id: ConnectorId): void {
    this.#state = { ...this.#state, [id]: { status: 'idle' } };
    saveState(this.#state);
    if (this.#preview?.id === id) this.#preview = null;
    this.#render();
  }

  #render(): void {
    const cards = DIAL_CONNECTORS.map((c) => this.#card(c)).join('');
    const preview = this.#preview ? this.#renderPreview(this.#preview) : '';
    this.#shadow.innerHTML = `
      <style>${STYLES}</style>
      <section class="connect" aria-labelledby="connect-title">
        <header>
          <h2 id="connect-title">Dial ko zinda karo</h2>
          <p>Aapke contacts, calendar, WhatsApp chat — sab khud-ba-khud relationship graph mein. Sab encrypted on-device. Demo mode mein realistic Indian data milta hai abhi.</p>
        </header>
        <ul class="grid">${cards}</ul>
        ${preview}
        <p class="note">Demo mode active. File-drop inputs (WhatsApp ZIP, LinkedIn ZIP, .vcf) abhi se kaam karte hain. Gmail / Calendar / Contacts OAuth live mode v1.1 mein PKCE proxy ke saath.</p>
      </section>
    `;
    this.#wire();
  }

  #wire(): void {
    this.#shadow.querySelectorAll<HTMLButtonElement>('[data-connect]').forEach((btn) => {
      btn.addEventListener('click', () => this.#connect(btn.dataset.connect as ConnectorId));
    });
    this.#shadow.querySelectorAll<HTMLButtonElement>('[data-disconnect]').forEach((btn) => {
      btn.addEventListener('click', () => this.#disconnect(btn.dataset.disconnect as ConnectorId));
    });
    this.#shadow.querySelectorAll<HTMLInputElement>('[data-file]').forEach((inp) => {
      inp.addEventListener('change', () => {
        const id = inp.dataset.file as ConnectorId;
        const f = inp.files?.[0];
        if (f) void this.#connect(id, f);
      });
    });
    this.#shadow.querySelector('[data-close-preview]')?.addEventListener('click', () => {
      this.#preview = null;
      this.#render();
    });
  }

  #card(meta: ConnectMeta): string {
    const s = this.#state[meta.id] ?? { status: 'idle' };
    const busy = this.#busy === meta.id;
    const connected = s.status === 'connected';
    const badge = connected
      ? `<span class="badge ok">Connected · ${s.count ?? 0} items</span>`
      : `<span class="badge dim">Not connected</span>`;
    const isFile = FILE_DROP_IDS.has(meta.id);
    const accept = meta.id === 'apple-contacts' ? '.vcf,text/vcard' : '.zip,.txt,application/zip';
    const action = busy
      ? `<button class="btn busy" disabled>Importing…</button>`
      : connected
        ? `<button class="btn ghost" data-disconnect="${meta.id}">Disconnect</button>`
        : isFile
          ? `<label class="btn primary"><input type="file" hidden data-file="${meta.id}" accept="${accept}" />Upload file</label><button class="btn ghost" data-connect="${meta.id}">Or Demo</button>`
          : `<button class="btn primary" data-connect="${meta.id}">Connect (Demo)</button>`;
    const titleHi = meta.title_hi ? `<span class="devanagari">${meta.title_hi}</span>` : '';
    return `
      <li class="card" data-id="${meta.id}">
        <header>
          <h3>${meta.title_en} ${titleHi}</h3>
          ${badge}
        </header>
        <p>${meta.description}</p>
        <footer>
          <span class="purpose">${meta.purposes[0]?.split('.').pop() ?? ''}</span>
          <span class="actions">${action}</span>
        </footer>
      </li>
    `;
  }

  #renderPreview(p: { id: ConnectorId; data: PreviewKind }): string {
    const rows: string[] = [];
    if (p.data.kind === 'contacts') {
      for (const c of p.data.rows.slice(0, 8)) {
        rows.push(`<tr><td>${escapeHtml(c.name)}</td><td>${c.phones[0] ?? ''}</td><td>${c.emails[0] ?? ''}</td><td>${c.relation_hint ?? ''}</td><td>${c.birthday ?? ''}</td></tr>`);
      }
      return `<aside class="preview"><header><h4>Preview · Contacts (${p.data.rows.length})</h4><button data-close-preview>×</button></header><table><thead><tr><th>Name</th><th>Phone</th><th>Email</th><th>Hint</th><th>Birthday</th></tr></thead><tbody>${rows.join('')}</tbody></table></aside>`;
    }
    if (p.data.kind === 'events') {
      for (const e of p.data.rows.slice(0, 8)) {
        rows.push(`<tr><td>${escapeHtml(e.title)}</td><td>${e.kind}</td><td>${e.start.slice(0, 10)}</td><td>${e.recurrence ?? ''}</td></tr>`);
      }
      return `<aside class="preview"><header><h4>Preview · Calendar (${p.data.rows.length})</h4><button data-close-preview>×</button></header><table><thead><tr><th>Title</th><th>Kind</th><th>Date</th><th>Recurrence</th></tr></thead><tbody>${rows.join('')}</tbody></table></aside>`;
    }
    if (p.data.kind === 'interactions') {
      for (const x of p.data.rows.slice(0, 8)) {
        rows.push(`<tr><td>${x.contact_key}</td><td>${x.month}</td><td>${x.messages_out}/${x.messages_in}</td><td>${x.calls_out + x.calls_in}</td><td>${x.longest_gap_days}d</td></tr>`);
      }
      return `<aside class="preview"><header><h4>Preview · Interactions (${p.data.rows.length})</h4><button data-close-preview>×</button></header><table><thead><tr><th>Contact</th><th>Month</th><th>Msg out/in</th><th>Calls</th><th>Longest gap</th></tr></thead><tbody>${rows.join('')}</tbody></table></aside>`;
    }
    if (p.data.kind === 'linkedin') {
      for (const c of p.data.rows.contacts.slice(0, 5)) rows.push(`<tr><td>${escapeHtml(c.name)}</td><td>contact</td><td>${c.emails[0] ?? ''}</td></tr>`);
      for (const w of p.data.rows.work.slice(0, 5)) rows.push(`<tr><td>${escapeHtml(w.company)}</td><td>${w.title}</td><td>${w.location ?? ''}</td></tr>`);
      return `<aside class="preview"><header><h4>Preview · LinkedIn (${p.data.rows.contacts.length} contacts, ${p.data.rows.work.length} positions)</h4><button data-close-preview>×</button></header><table><thead><tr><th>Item</th><th>Kind</th><th>Detail</th></tr></thead><tbody>${rows.join('')}</tbody></table></aside>`;
    }
    if (p.data.kind === 'trips') {
      for (const t of p.data.rows.slice(0, 8)) {
        rows.push(`<tr><td>${t.merchant}</td><td>${t.origin}</td><td>${t.destination}</td><td>${(t.passengers ?? []).join(', ')}</td><td>₹${(t.fare.paise / 100).toFixed(0)}</td></tr>`);
      }
      return `<aside class="preview"><header><h4>Preview · Rides (${p.data.rows.length})</h4><button data-close-preview>×</button></header><table><thead><tr><th>Source</th><th>From</th><th>To</th><th>With</th><th>Fare</th></tr></thead><tbody>${rows.join('')}</tbody></table></aside>`;
    }
    for (const t of p.data.rows.slice(0, 8)) {
      rows.push(`<tr><td>${t.merchant}</td><td>${t.kind}</td><td>${t.destination}</td><td>${t.travellers.join(', ')}</td><td>₹${(t.cost.paise / 100).toFixed(0)}</td></tr>`);
    }
    return `<aside class="preview"><header><h4>Preview · Travel (${p.data.rows.length})</h4><button data-close-preview>×</button></header><table><thead><tr><th>Source</th><th>Kind</th><th>Destination</th><th>Travellers</th><th>Cost</th></tr></thead><tbody>${rows.join('')}</tbody></table></aside>`;
  }
}

async function runConnector(id: ConnectorId, file?: File): Promise<PreviewKind> {
  if (id === 'gcalendar') return { kind: 'events', rows: await fetchCalendarEvents({ mode: 'mock' }) };
  if (id === 'gcontacts') return { kind: 'contacts', rows: await fetchGContacts({ mode: 'mock' }) };
  if (id === 'apple-contacts') {
    if (file) return { kind: 'contacts', rows: await parseVcard(file) };
    return { kind: 'contacts', rows: await fetchGContacts({ mode: 'mock' }) };
  }
  if (id === 'wa-chat-export') {
    if (file) return { kind: 'interactions', rows: await parseWaChat({ mode: 'file', file }) };
    return { kind: 'interactions', rows: await parseWaChat({ mode: 'mock' }) };
  }
  if (id === 'linkedin') {
    if (file) return { kind: 'linkedin', rows: await parseLinkedInZip({ mode: 'file', file }) };
    return { kind: 'linkedin', rows: await parseLinkedInZip({ mode: 'mock' }) };
  }
  if (id === 'uber-ola') return { kind: 'trips', rows: await fetchTrips({ mode: 'mock' }) };
  if (id === 'mmt-travel') return { kind: 'travel', rows: await fetchTravel({ mode: 'mock' }) };
  throw new Error(`unknown connector: ${id}`);
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function loadState(): Record<ConnectorId, ConnectorBadge> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<ConnectorId, ConnectorBadge>) : ({} as Record<ConnectorId, ConnectorBadge>);
  } catch { return {} as Record<ConnectorId, ConnectorBadge>; }
}
function saveState(state: Record<ConnectorId, ConnectorBadge>): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* swallow */ }
}

const STYLES = `
:host { display: block; container-type: inline-size; }
.connect {
  --rose: oklch(70% 0.10 18);
  --paper: oklch(98% 0.012 92);
  --ink: oklch(22% 0.02 60);
  --line: color-mix(in oklch, var(--ink) 12%, transparent);
  background: var(--paper);
  color: var(--ink);
  padding: 2.4rem clamp(1rem, 3vw, 2.4rem);
  border-block: 1px solid var(--line);
  font-family: 'EB Garamond', 'Tiro Devanagari Hindi', Georgia, serif;
}
header h2 {
  font-size: clamp(1.6rem, 3.2vw, 2.4rem);
  font-weight: 500;
  letter-spacing: -0.01em;
  margin: 0 0 0.4rem;
}
header p { margin: 0 0 1.6rem; opacity: 0.78; max-width: 60ch; }
.grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 24rem), 1fr));
  gap: 1rem;
}
.card {
  position: relative;
  padding: 1.2rem 1.2rem 1.05rem;
  background: oklch(100% 0 0 / 0.5);
  border: 1px solid var(--line);
  border-radius: 6px;
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 0.7rem;
  transition: box-shadow 280ms cubic-bezier(0.2, 0.6, 0.2, 1), translate 200ms;
}
.card:hover {
  box-shadow: 0 2px 24px -8px color-mix(in oklch, var(--rose) 60%, transparent);
  translate: 0 -1px;
}
.card header { display: flex; justify-content: space-between; align-items: baseline; gap: 0.6rem; }
.card h3 { margin: 0; font-size: 1.05rem; font-weight: 600; letter-spacing: -0.005em; }
.devanagari { font-family: 'Tiro Devanagari Hindi', serif; opacity: 0.78; font-weight: 400; font-size: 0.92rem; margin-inline-start: 0.4rem; }
.card p { margin: 0; font-size: 0.9rem; line-height: 1.45; opacity: 0.82; }
.card footer { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; padding-block-start: 0.4rem; border-block-start: 1px dashed var(--line); }
.actions { display: inline-flex; gap: 0.4rem; align-items: center; flex-wrap: wrap; justify-content: flex-end; }
.purpose {
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  opacity: 0.55;
}
.badge { font-size: 0.7rem; letter-spacing: 0.06em; text-transform: uppercase; padding: 0.18rem 0.55rem; border-radius: 999px; font-family: 'JetBrains Mono', monospace; }
.badge.ok { background: color-mix(in oklch, var(--rose) 40%, transparent); color: oklch(34% 0.08 18); }
.badge.dim { background: color-mix(in oklch, var(--ink) 8%, transparent); opacity: 0.6; }
.btn {
  font: inherit;
  padding: 0.45rem 0.9rem;
  border-radius: 4px;
  border: 1px solid currentColor;
  background: transparent;
  cursor: pointer;
  font-size: 0.85rem;
  letter-spacing: 0.02em;
  transition: background 180ms, color 180ms;
}
.btn.primary {
  background: var(--ink);
  color: var(--paper);
  border-color: var(--ink);
  display: inline-flex;
  align-items: center;
}
.btn.primary:hover { background: oklch(28% 0.04 60); }
.btn.ghost { color: var(--ink); opacity: 0.75; }
.btn.ghost:hover { opacity: 1; }
.btn.busy { opacity: 0.7; cursor: progress; }
.preview {
  margin-block-start: 1.4rem;
  padding: 1.1rem 1.2rem;
  background: oklch(100% 0 0 / 0.7);
  border: 1px solid var(--line);
  border-radius: 6px;
}
.preview header { display: flex; justify-content: space-between; align-items: center; margin-block-end: 0.7rem; }
.preview h4 { margin: 0; font-size: 1rem; font-weight: 600; }
.preview button { background: transparent; border: none; cursor: pointer; font-size: 1.2rem; opacity: 0.7; }
.preview button:hover { opacity: 1; }
.preview table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
.preview th, .preview td { padding: 0.35rem 0.5rem; text-align: start; border-block-end: 1px solid var(--line); }
.preview th { font-family: 'JetBrains Mono', monospace; font-size: 0.72rem; letter-spacing: 0.06em; text-transform: uppercase; opacity: 0.6; font-weight: 500; }
.note { margin: 1.1rem 0 0; font-size: 0.78rem; opacity: 0.62; max-width: 64ch; }
@container (min-width: 70rem) {
  .connect { padding-inline: 3.2rem; }
}
`;

if (!customElements.get('dial-connect-section')) {
  customElements.define('dial-connect-section', DialConnectSection);
}
